import React, { useState, useCallback } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    Alert,
    CircularProgress,
    TextField,
    List,
    ListItem,
    ListItemButton,
    Divider,
    Pagination,
} from '@mui/material';
import { Inventory, CalendarToday, Inventory2, Undo } from '@mui/icons-material';
import { useBundleSearch, useDebundleSuggestions } from '../../hooks/useWarehouseData';
import { useSearchDebounce } from '../../hooks/useDebounce';
import { BundleSearchResult } from '../../types';
import { DebundleBundleInfo } from './types';
import { getAggregatedChildSKUs } from '../../utils/inventoryUtils';


interface DebundleBundleSelectorProps {
    selectedBundle: DebundleBundleInfo | null;
    onBundleSelect: (bundle: BundleSearchResult) => void;
}

const DebundleBundleSelector: React.FC<DebundleBundleSelectorProps> = ({
    selectedBundle,
    onBundleSelect,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const limit = 10;

    // Use custom debounce hook with 400ms delay
    const { debouncedSearch, isDebouncing } = useSearchDebounce(searchTerm, 400);

    // Reset to first page when search term changes
    React.useEffect(() => {
        setCurrentPage(0);
    }, [debouncedSearch]);

    // Fetch bundles using the new API - include inactive bundles for debundling
    const { data: bundleData, isLoading, error } = useBundleSearch(
        debouncedSearch, // No default search term
        currentPage,
        limit,
        true // includeInactive: true for debundle functionality
    );

    const handleSearch = useCallback((value: string) => {
        setSearchTerm(value);
    }, []);

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page - 1); // MUI Pagination is 1-indexed, API is 0-indexed
    };

    // New: Fetch suggestions for inactive bundles with stock
    const { data: suggestionsData, isLoading: isLoadingSuggestions } = useDebundleSuggestions();

    const handleBundleSelect = (bundle: BundleSearchResult) => {
        onBundleSelect(bundle);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const totalPages = bundleData ? Math.ceil(bundleData.total / limit) : 0;

    if (error) {
        return (
            <Alert severity="error">
                Failed to load bundles. Please try again later.
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Undo />
                Select Bundle to Debundle
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search and choose the bundle you want to break down into its component child SKUs
            </Typography>

            <Alert severity="info" sx={{ mb: 2, py: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mb: 0.5 }}>
                    💡 Search Tips: Type 3+ characters • Try: "heart", "gift", "box", "simple"
                </Typography>
            </Alert>

            <TextField
                fullWidth
                label="Search Bundle SKU"
                placeholder="Type at least 3 characters to search..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                sx={{ mb: 3 }}
                helperText={
                    isDebouncing && searchTerm.length >= 3
                        ? "Searching as you type..."
                        : searchTerm.length > 0 && searchTerm.length < 3
                            ? "Please enter at least 3 characters to search"
                            : debouncedSearch.length >= 3
                                ? `${bundleData?.total || 0} bundles found`
                                : "Enter at least 3 characters to search for bundles"
                }
            />

            {/* Show debouncing indicator while user is typing */}
            {isDebouncing && searchTerm.length >= 3 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography sx={{ ml: 2 }} variant="body2" color="text.secondary">
                        Typing...
                    </Typography>
                </Box>
            )}

            {/* Show loading indicator while API call is in progress */}
            {isLoading && debouncedSearch.length >= 3 && !isDebouncing && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Searching bundles...</Typography>
                </Box>
            )}

            {debouncedSearch.length > 0 && debouncedSearch.length < 3 && !isDebouncing && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Please enter at least 3 characters to search
                </Alert>
            )}

            {/* Suggested Bundles Section - Shown when not searching or no results found */}
            {(!debouncedSearch || debouncedSearch.length < 3) && !isDebouncing && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontWeight: 'bold' }}>
                        <Inventory />
                        Suggested Inactive Bundles (with stock)
                    </Typography>

                    {isLoadingSuggestions ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">Loading suggestions...</Typography>
                        </Box>
                    ) : suggestionsData?.data && suggestionsData.data.length > 0 ? (
                        <List sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            {suggestionsData.data.map((bundle) => (
                                <ListItem key={bundle.id} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleBundleSelect(bundle)}
                                        selected={selectedBundle?.bundleSku === bundle.bundleSku}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: selectedBundle?.bundleSku === bundle.bundleSku ? 'primary.main' : 'divider',
                                            '&:hover': { borderColor: 'primary.main' }
                                        }}
                                    >
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold">{bundle.bundleSku}</Typography>
                                                <Typography variant="caption" color="text.secondary">{bundle.name}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Chip
                                                    size="small"
                                                    label={`${(bundle as any).totalStock || 0} in stock`}
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {bundle.products.length} components
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                            No inactive bundles with stock found locally.
                        </Typography>
                    )}
                </Box>
            )}

            {bundleData && bundleData.data.length === 0 && !isLoading && debouncedSearch.length >= 3 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography>
                        No bundles found for "{debouncedSearch}"
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Try searching with different keywords like "heart", "gift", "box"
                    </Typography>
                </Alert>
            )}

            {bundleData && bundleData.data.length > 0 && debouncedSearch.length >= 3 && (
                <>
                    <List sx={{ maxHeight: '400px', overflow: 'auto', mb: 2 }}>
                        {bundleData.data.map((bundle, index) => (
                            <React.Fragment key={bundle.id}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleBundleSelect(bundle)}
                                        selected={selectedBundle?.bundleSku === bundle.bundleSku}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 1,
                                            border: '1px solid',
                                            borderColor: selectedBundle?.bundleSku === bundle.bundleSku ? 'primary.main' : 'divider',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                                <Typography variant="h6" component="div">
                                                    {bundle.bundleSku}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    icon={<Inventory2 />}
                                                    label={`${bundle.products.length} Products`}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>

                                            {bundle.name && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {bundle.name}
                                                </Typography>
                                            )}

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                {getAggregatedChildSKUs(
                                                    bundle.products.map(p => ({ skuId: p, name: p, requiredQuantity: 1 }))
                                                ).slice(0, 5).map((product) => (
                                                    <Chip
                                                        key={product.skuId}
                                                        label={product.requiredQuantity > 1 ? `${product.skuId} (${product.requiredQuantity})` : product.skuId}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {bundle.products.length > 5 && (
                                                    <Chip
                                                        label={`+${bundle.products.length - 5} more`}
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                    />
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <CalendarToday fontSize="small" color="disabled" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Created: {formatDate(bundle.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                                {index < bundleData.data.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Selected Bundle Details */}
            {selectedBundle && (
                <Card variant="outlined" sx={{ mt: 3, bgcolor: 'primary.light', borderColor: 'primary.main', border: '2px solid' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                            Selected: {selectedBundle.name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.8)' }}>
                            SKU: {selectedBundle.bundleSku}
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: 'white' }}>
                            Child SKUs ({selectedBundle.childSKUs.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedBundle.childSKUs.map((childSKU) => (
                                <Chip
                                    key={childSKU.skuId}
                                    label={`${childSKU.skuId} (${childSKU.requiredQuantity})`}
                                    variant="filled"
                                    size="small"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default DebundleBundleSelector;
